// ============================================================
// SFV Tech | Shop Goods Page ("Goods in the Shop")
// Access: strictly admin + staff. Selling an item here reduces its
// quantity and logs the sale — see /api/shop-goods/:id/sell.
// ============================================================

import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TablePagination,
  Avatar,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Sell as SellIcon,
  WarningAmber,
  Layers,
  AttachMoney,
  Search,
  History,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

const ALLOWED_ROLES = ["admin", "staff"];

export default function ShopGoodsPage() {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const token = localStorage.getItem("token");

  const role = String(user?.role || "").toLowerCase();
  const hasAccess = ALLOWED_ROLES.includes(role);

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Tabs: 0 = Inventory, 1 = Selling Records
  const [tab, setTab] = useState(0);

  // Selling records (separate pagination from the inventory table)
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [salesPage, setSalesPage] = useState(0);
  const [salesRowsPerPage, setSalesRowsPerPage] = useState(20);
  const [salesTotal, setSalesTotal] = useState(0);

  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [openSell, setOpenSell] = useState(false);
  const [activeItem, setActiveItem] = useState(null);

  const [form, setForm] = useState({ name: "", category: "", unit: "", unit_price: "", quantity: "" });
  const [sellForm, setSellForm] = useState({ qty: "", customer_name: "", note: "" });

  const COLORS = { accent: "#00c896", accentDark: "#00806a", border: "#b9fbc0" };

  const money = (v) => `₦${Number(v || 0).toLocaleString()}`;

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/shop-goods?page=${page + 1}&limit=${rowsPerPage}&search=${search}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      setTotalItems(res.data.pagination?.total_items || 0);
    } catch (err) {
      enqueueSnackbar("Failed to fetch shop goods", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.get("/api/shop-goods/summary", { headers: { Authorization: `Bearer ${token}` } });
      setSummary(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSales = async () => {
    try {
      setSalesLoading(true);
      const res = await api.get(`/api/shop-goods/sales?page=${salesPage + 1}&limit=${salesRowsPerPage}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSales(res.data.sales || []);
      setSalesTotal(res.data.pagination?.total_items || 0);
    } catch (err) {
      enqueueSnackbar("Failed to fetch selling records", { variant: "error" });
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    if (!hasAccess) return;
    fetchItems();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, hasAccess]);

  useEffect(() => {
    if (!hasAccess || tab !== 1) return;
    fetchSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, salesPage, salesRowsPerPage, hasAccess]);

  if (!hasAccess) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h5" fontWeight={800} color="error">
          Access Denied
        </Typography>
        <Typography sx={{ mt: 1 }}>This page is only available to admin and staff accounts.</Typography>
      </Box>
    );
  }

  const handleAdd = async () => {
    try {
      await api.post("/api/shop-goods/add", form, { headers: { Authorization: `Bearer ${token}` } });
      enqueueSnackbar("Item added", { variant: "success" });
      setOpenAdd(false);
      setForm({ name: "", category: "", unit: "", unit_price: "", quantity: "" });
      fetchItems();
      fetchSummary();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || "Failed to add item", { variant: "error" });
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/api/shop-goods/update/${activeItem.id}`, form, { headers: { Authorization: `Bearer ${token}` } });
      enqueueSnackbar("Item updated", { variant: "success" });
      setOpenEdit(false);
      fetchItems();
      fetchSummary();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || "Failed to update item", { variant: "error" });
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/shop-goods/delete/${activeItem.id}`, { headers: { Authorization: `Bearer ${token}` } });
      enqueueSnackbar("Item deleted", { variant: "success" });
      setOpenDelete(false);
      fetchItems();
      fetchSummary();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || "Failed to delete item", { variant: "error" });
    }
  };

  const handleSell = async () => {
    try {
      const res = await api.post(
        `/api/shop-goods/${activeItem.id}/sell`,
        { qty: Number(sellForm.qty), customer_name: sellForm.customer_name, note: sellForm.note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      enqueueSnackbar(`Sold ${sellForm.qty} x ${activeItem.name} -- ${res.data.remaining_quantity} left`, { variant: "success" });
      setOpenSell(false);
      setSellForm({ qty: "", customer_name: "", note: "" });
      fetchItems();
      fetchSummary();
      fetchSales();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.error || "Failed to record sale", { variant: "error" });
    }
  };

  const openEditDialog = (item) => {
    setActiveItem(item);
    setForm({ name: item.name, category: item.category || "", unit: item.unit || "", unit_price: item.unit_price, quantity: item.quantity });
    setOpenEdit(true);
  };

  const openSellDialog = (item) => {
    setActiveItem(item);
    setSellForm({ qty: "", customer_name: "", note: "" });
    setOpenSell(true);
  };

  const stockColor = (qty) => (qty === 0 ? "error" : qty < 10 ? "warning" : "success");

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={800}>Goods in the Shop</Typography>
        <Button variant="contained" startIcon={<Add />} sx={{ bgcolor: COLORS.accent }} onClick={() => setOpenAdd(true)}>
          Add Item
        </Button>
      </Stack>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        textColor="inherit"
        TabIndicatorProps={{ style: { backgroundColor: COLORS.accent } }}
        sx={{
          mt: 2,
          borderBottom: 1,
          borderColor: "rgba(255,255,255,0.15)",
          "& .MuiTab-root": {
            color: "#cfd8dc",
            fontWeight: 700,
            textTransform: "none",
          },
          "& .Mui-selected": {
            color: `${COLORS.accent} !important`,
          },
        }}
      >
        <Tab icon={<Layers fontSize="small" />} iconPosition="start" label="Inventory" />
        <Tab icon={<History fontSize="small" />} iconPosition="start" label="Selling Records" />
      </Tabs>

      {tab === 0 && (
      <>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {[
          { title: "Total Items", value: summary.total_items, icon: <Layers /> },
          { title: "Units in Stock", value: summary.total_units, icon: <Layers /> },
          { title: "Low Stock (<10)", value: summary.low_stock, icon: <WarningAmber /> },
          { title: "Total Sales Revenue", value: money(summary.total_revenue), icon: <AttachMoney /> },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ border: `1px solid ${COLORS.border}`, borderRadius: 3 }}>
              <CardContent sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Avatar sx={{ bgcolor: COLORS.accent }}>{s.icon}</Avatar>
                <Box>
                  <Typography variant="body2">{s.title}</Typography>
                  <Typography variant="h5" fontWeight={800} color={COLORS.accentDark}>{s.value ?? 0}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name or category..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{ startAdornment: <Search sx={{ mr: 1 }} /> }}
        />
      </Box>

      <Box sx={{ mt: 3, background: "#fff", borderRadius: 3 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {["Name", "Category", "Unit (m/yards)", "Unit Price", "Quantity", "Status", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>{money(item.unit_price)}</TableCell>
                  <TableCell>
                    <Chip label={item.quantity} color={stockColor(item.quantity)} />
                  </TableCell>
                  <TableCell>
                    {item.quantity === 0 ? (
                      <Chip label="Out of Stock" color="error" size="small" />
                    ) : item.quantity < 10 ? (
                      <Chip label="Low" color="warning" size="small" />
                    ) : (
                      <Chip label="OK" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Sell">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={item.quantity === 0}
                            onClick={() => openSellDialog(item)}
                          >
                            <SellIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(item)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => { setActiveItem(item); setOpenDelete(true); }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Box>
      </>
      )}

      {tab === 1 && (
        <Box sx={{ mt: 3, background: "#fff", borderRadius: 3 }}>
          {salesLoading ? (
            <Box sx={{ textAlign: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : sales.length === 0 ? (
            <Typography sx={{ p: 3, textAlign: "center" }} color="text.secondary">
              No sales recorded yet.
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  {["Date", "Item", "Qty Sold", "Unit Price", "Total", "Sold By", "Customer", "Note"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.map((sale) => (
                  <TableRow key={sale.id} hover>
                    <TableCell>{new Date(sale.sold_at).toLocaleString()}</TableCell>
                    <TableCell>{sale.item_name}</TableCell>
                    <TableCell>{sale.qty_sold}</TableCell>
                    <TableCell>{money(sale.unit_price)}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{money(sale.total_amount)}</TableCell>
                    <TableCell>{sale.sold_by}</TableCell>
                    <TableCell>{sale.customer_name || "-"}</TableCell>
                    <TableCell>{sale.note || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <TablePagination
            component="div"
            count={salesTotal}
            page={salesPage}
            onPageChange={(_, p) => setSalesPage(p)}
            rowsPerPage={salesRowsPerPage}
            onRowsPerPageChange={(e) => { setSalesRowsPerPage(parseInt(e.target.value, 10)); setSalesPage(0); }}
            rowsPerPageOptions={[20, 50, 100]}
          />
        </Box>
      )}

      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Shop Item</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {["name", "category", "unit"].map((f) => (
              <Grid item xs={12} sm={6} key={f}>
                <TextField
                  label={f.charAt(0).toUpperCase() + f.slice(1)}
                  fullWidth
                  value={form[f]}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Unit Price"
                type="number"
                fullWidth
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Shop Item</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {["name", "category", "unit"].map((f) => (
              <Grid item xs={12} sm={6} key={f}>
                <TextField
                  label={f.charAt(0).toUpperCase() + f.slice(1)}
                  fullWidth
                  value={form[f]}
                  onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Unit Price"
                type="number"
                fullWidth
                value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Quantity"
                type="number"
                fullWidth
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                helperText="Only use this to correct stock (e.g. a count error) -- use Sell to record an actual sale."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEdit}>Update</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSell} onClose={() => setOpenSell(false)} fullWidth maxWidth="xs">
        <DialogTitle>Sell {activeItem?.name}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {activeItem?.quantity} unit(s) currently in stock at {money(activeItem?.unit_price)} each.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Quantity to Sell"
              type="number"
              fullWidth
              value={sellForm.qty}
              onChange={(e) => setSellForm({ ...sellForm, qty: e.target.value })}
            />
            <TextField
              label="Customer Name (optional)"
              fullWidth
              value={sellForm.customer_name}
              onChange={(e) => setSellForm({ ...sellForm, customer_name: e.target.value })}
            />
            <TextField
              label="Note (optional)"
              fullWidth
              value={sellForm.note}
              onChange={(e) => setSellForm({ ...sellForm, note: e.target.value })}
            />
            {sellForm.qty && activeItem && (
              <Typography fontWeight={700}>
                Total: {money(Number(sellForm.qty) * Number(activeItem.unit_price))}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSell(false)}>Cancel</Button>
          <Button variant="contained" color="success" startIcon={<SellIcon />} onClick={handleSell}>
            Confirm Sale
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <Typography>Delete <strong>{activeItem?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
